// Shared "Waardebon geldig voor X personen / X volwassenen + Y kinderen"
// label resolver. Used by VoucherHeroPrice, FinalCTA, and SavingsCallout
// so the three surfaces always agree. The source of truth for adults +
// children comes from the backend voucher-campaign payload (adults Int
// default 2, children Int default 0). Legacy campaigns therefore keep
// rendering "totaal voor 2 personen" exactly as before.
//
// The translator `t` argument is the function returned by
// getTranslations(locale).t — passed in to avoid a hard dependency on
// SiteContext from this leaf utility.

export function resolveVoucherPaxLabel(t, adultsRaw, childrenRaw) {
  const adults = sanitizePax(adultsRaw, 2);
  const children = sanitizePax(childrenRaw, 0);

  // Backwards-compatible fast path: legacy 2 / 0 → keep the historical
  // key so any operator-customised override of total_for_2 still wins.
  if (adults === 2 && children === 0) {
    return t("voucher.total_for_2");
  }
  if (children > 0) {
    return t("voucher.total_for_adults_children", { adults, children });
  }
  return t("voucher.total_for_persons", { adults });
}

/**
 * Quantity-aware sibling of resolveVoucherPaxLabel, for a TOTAL amount that
 * covers `quantity` separate vouchers (voucher-checkout's "quantity" select,
 * 1-5) rather than a single voucher's own price.
 *
 * Independent-reviewer finding on PR #1199 (audit-cluster 7 taak #37,
 * head 535420eda): the checkout's order-summary TOTAL row and the mobile
 * bottom bar show `totalAfterDiscount` (price * quantity - discount), but
 * originally carried the SAME `paxLabel` as the per-voucher price rows —
 * correct at quantity=1 (where per-voucher and total coincide), silently
 * WRONG above it: 3 vouchers of 199 read "€ 597,- / totaal voor 2
 * personen", implying the €597 total covers 2 people when it actually
 * covers 3 separate 2-person vouchers (6 people). Same bug class the
 * owner-rule chain already caught once before in the OTHER direction
 * (#1193 round 6: a per-person amount mislabelled as a 2-person total).
 *
 * Deliberately NOT `resolveVoucherPaxLabel(t, adults * qty, children * qty)`
 * — reviewer's own analysis: that reads correctly arithmetically ("totaal
 * voor 6 personen") but SUGGESTS one big voucher for 6 people instead of
 * 3 separate gifts, which is its own kind of wrong. Phrasing here instead
 * follows the reviewer's preferred reading: "{quantity} vouchers, elk
 * totaal voor 2 personen" — explicit about there being `quantity` distinct
 * vouchers, each individually covering its own pax count.
 *
 * At quantity <= 1 this returns EXACTLY resolveVoucherPaxLabel's own
 * output (delegates to it, not a reimplementation) — the per-voucher and
 * total labels are identical text for a single voucher, so there is
 * nothing quantity-specific to say and no behaviour change for the
 * (overwhelmingly common) single-voucher purchase.
 *
 * Independent-reviewer finding, round 3 (PR #1199, head ce8e6b390):
 * Polish (a Slavic numeral-noun-agreement language) needs a DIFFERENT
 * plural form of "voucher" depending on the count — "2/3/4 vouchery"
 * (paucal/"few" form) but "5 voucherów" (genitive/"many" form), and the
 * checkout's own quantity <select> renders exactly [1,2,3,4,5], so 5 is
 * directly reachable and was rendering the wrong form. This file already
 * establishes the fix pattern for exactly this class of problem —
 * resolveVoucherPaxPhrase's own doc-comment above: "locales with
 * numeral-dependent noun forms... can phrase it precisely" via a
 * SEPARATE i18n key per form, not a general pluralisation library. Same
 * technique here: a "_many" sibling key per branch, selected by quantity.
 * Every locale needs both keys (nl/en/de/fr repeat the same text in both
 * — they don't distinguish this range grammatically; only pl differs).
 * Written as the general "outside 2-4" rule rather than hardcoded to
 * literally 5, so it stays correct if the select's cap is ever raised
 * (Polish "few" is exactly {2,3,4} and any two-digit number ending in
 * 2-4 except the 12-14 teens; "many" is everything else, including 5-9,
 * which is the only additional range reachable if the cap grows to 9).
 */
export function resolveVoucherTotalPaxLabel(t, adultsRaw, childrenRaw, quantityRaw) {
  const quantity = sanitizePax(quantityRaw, 1) || 1;
  if (quantity <= 1) {
    return resolveVoucherPaxLabel(t, adultsRaw, childrenRaw);
  }
  const adults = sanitizePax(adultsRaw, 2);
  const children = sanitizePax(childrenRaw, 0);
  const isFew = quantity >= 2 && quantity <= 4;
  if (adults === 2 && children === 0) {
    return isFew
      ? t("voucher.total_for_2_qty", { quantity })
      : t("voucher.total_for_2_qty_many", { quantity });
  }
  if (children > 0) {
    return isFew
      ? t("voucher.total_for_adults_children_qty", { quantity, adults, children })
      : t("voucher.total_for_adults_children_qty_many", { quantity, adults, children });
  }
  return isFew
    ? t("voucher.total_for_persons_qty", { quantity, adults })
    : t("voucher.total_for_persons_qty_many", { quantity, adults });
}

/**
 * "each [voucher] <pax label>" — quantity-aware sibling for contexts that
 * ALREADY state the voucher count elsewhere and would otherwise repeat it
 * redundantly if resolveVoucherTotalPaxLabel were used as-is.
 *
 * Independent-reviewer copy observation, round 3 (PR #1199, head
 * ce8e6b390): the checkout's mobile fixed bottom bar stacks THREE lines —
 * the total price, `mobileVoucherLabel` ("per {quantity} vouchers"), and
 * (round 2's fix) the quantity-aware total-pax label
 * ("{quantity} vouchers, elk totaal voor 2 personen") — so "3 vouchers"
 * appeared twice in two adjacent lines. This resolver drops the leading
 * "{quantity} vouchers" clause (the count is already on the line above)
 * and keeps only the "each voucher covers N people" part, which is the
 * information the surface still needs (per round 2's fix: a bare
 * per-voucher label under a TOTAL amount is the actual bug being
 * guarded against).
 *
 * At quantity <= 1 this is EXACTLY resolveVoucherPaxLabel's own output —
 * no "each" prefix makes sense for a single voucher, and there is no
 * adjacent count to deduplicate against.
 *
 * Bonus: because this phrase never states the voucher COUNT as a bare
 * number, it has no Slavic numeral-noun-agreement surface at all — PL's
 * "każdy" (each) + the existing "dla {n} osób" phrasing is already
 * count-invariant (the preposition "dla" governs the genitive case
 * regardless of the number, unlike a bare-counted noun such as
 * "vouchery"/"voucherów" above), so this function needed no PL few/many
 * split of its own.
 */
export function resolveVoucherEachPaxLabel(t, adultsRaw, childrenRaw, quantityRaw) {
  const quantity = sanitizePax(quantityRaw, 1) || 1;
  const base = resolveVoucherPaxLabel(t, adultsRaw, childrenRaw);
  if (quantity <= 1) {
    return base;
  }
  return `${t("voucher.checkout.each_voucher_prefix")} ${base}`;
}

/**
 * Bare pax phrase ("2 personen" / "{adults} volwassenen + {children}
 * kinderen") for sentence templates that carry their own preposition,
 * e.g. "Voucher voor {pax}" and "Op basis van {pax}" in the
 * VoucherCostBreakdown (TD-blocks 2026-07-02). The design assumed
 * resolveVoucherPaxLabel returned "voor 2 personen", but it actually
 * returns "totaal voor 2 personen" (a full label, not a phrase), which
 * would double up as "Voucher voor totaal voor 2 personen". This
 * sibling shares the exact same sanitizePax defaults (2 adults / 0
 * children) so the numbers always agree with the hero.
 */
export function resolveVoucherPaxPhrase(t, adultsRaw, childrenRaw) {
  const adults = sanitizePax(adultsRaw, 2);
  const children = sanitizePax(childrenRaw, 0);
  // Singular child gets its own key (review nit PR #1006): "1 kinderen"
  // / "1 Kinder" is grammatically wrong; locales phrase 1 kind / 1 Kind
  // / 1 enfant / 1 child / 1 dziecko via voucher.pax.adults_child_1.
  if (children === 1) {
    return t("voucher.pax.adults_child_1", { adults });
  }
  if (children > 0) {
    return t("voucher.pax.adults_children", { adults, children });
  }
  // Separate fast-path key for the dominant 2-adults case so locales
  // with numeral-dependent noun forms (PL "2 osób" vs "{n} osób") can
  // phrase it precisely, mirroring resolveVoucherPaxLabel's structure.
  if (adults === 2) {
    return t("voucher.pax.persons_2");
  }
  return t("voucher.pax.persons_n", { persons: adults });
}

function sanitizePax(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.floor(n);
}

/**
 * Per-person price for the voucher (Google Ads fase 2, 2026-06-10).
 *
 * Why: resort-intent searchers compare against per-person entree prices
 * (~EUR 30-40); the hero shows only the 2-person total (e.g. EUR 149),
 * which reads expensive at first glance and contributed to the
 * "Landing Page Experience: below average" rating in Google Ads (see
 * favotrip-handbook handover/sessions/dennis/
 * 2026-06-10-google-ads-fase0-feitenrapport.md). Showing the derived
 * per-person price is additive, factual, and uses the SAME converted
 * price the hero already renders (no second price source, ACM-safe).
 *
 * Returns the per-person amount, or null when it should not be shown
 * (missing/invalid price, or fewer than 2 travellers, where "p.p."
 * framing adds nothing).
 */
export function perPersonPrice(convertedPrice, adultsRaw, childrenRaw) {
  const price = Number(convertedPrice);
  if (!Number.isFinite(price) || price <= 0) return null;
  const adults = sanitizePax(adultsRaw, 2);
  const children = sanitizePax(childrenRaw, 0);
  const persons = adults + children;
  if (persons < 2) return null;
  // Round to cents; formatPriceText handles ",-" vs decimals display.
  // NB: rounds independently of the total (100/3 -> 33.33; x3 = 99.99).
  // Display-only by design. Never sum per-person values back up to a
  // total on any surface; the hero total stays the single source.
  return Math.round((price / persons) * 100) / 100;
}
